import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import useAuth from '@/hooks/useAuth';

const navItems = [
  { to: '/logista/dashboard', label: 'Dashboard', icon: '📊' },
  { to: '/logista/loja', label: 'A minha loja', icon: '🏪' },
  { to: '/logista/ofertas', label: 'Ofertas', icon: '🎁' },
  { to: '/logista/resgates', label: 'Resgates', icon: '🎫' },
  { to: '/logista/analytics', label: 'Analytics', icon: '📈' },
  { to: '/logista/subscription', label: 'Subscrição', icon: '💳' },
];

function SubscriptionBadge({ status, daysLeft }) {
  if (status === 'trial') {
    return (
      <span className="ml-auto rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">
        Trial {daysLeft}d
      </span>
    );
  }
  if (status === 'active') {
    return (
      <span className="ml-auto rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
        Ativo
      </span>
    );
  }
  return (
    <span className="ml-auto rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">
      Expirado
    </span>
  );
}

export default function LogistaLayout() {
  const { handleLogout, user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  // subscription state is loaded on dashboard/subscription pages; pass via context if needed
  // For the badge in sidebar we read from sessionStorage after /me call
  const subStatus = null;

  const sidebar = (
    <aside className="flex h-full w-72 max-w-[82vw] flex-shrink-0 flex-col border-r border-accent/20 bg-surface text-fg shadow-lg md:w-60">
      <div className="border-b border-accent/15 bg-accent px-4 py-4">
        <div className="font-serif text-lg font-bold text-white">⚓ Hakken City</div>
        <div className="mt-0.5 text-xs text-white/70">Portal do Logista</div>
      </div>

      {user && (
        <div className="flex items-center gap-3 border-b border-edge px-4 py-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent/10 text-lg font-bold text-accent">
            {(user.email?.[0] || 'L').toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-bold text-fg">{user.email}</p>
            <p className="text-xs text-muted">Logista</p>
          </div>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto py-2">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => setMenuOpen(false)}
            className={({ isActive }) =>
              `flex min-h-11 items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                isActive
                  ? 'border-r-2 border-accent bg-accent/8 font-bold text-accent'
                  : 'text-muted hover:bg-background hover:text-fg'
              }`
            }
          >
            <span className="w-5 text-center text-base" aria-hidden="true">{item.icon}</span>
            <span className="truncate">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <button
        type="button"
        onClick={handleLogout}
        className="border-t border-edge px-4 py-3 text-left text-sm font-semibold text-red-500 hover:bg-red-50 hover:text-red-600"
      >
        🚪 Terminar sessão
      </button>
    </aside>
  );

  return (
    <div className="h-full bg-background font-body text-fg md:flex">
      <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-edge bg-surface px-4 shadow-sm md:hidden">
        <div>
          <div className="font-serif text-base font-bold text-accent">⚓ Portal do Logista</div>
          <div className="text-[11px] text-muted">Hakken City</div>
        </div>
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          className="rounded-lg border border-accent/30 bg-accent/10 px-3 py-2 text-sm font-bold text-accent"
          aria-label="Abrir menu"
        >
          Menu
        </button>
      </header>

      <div className="hidden md:block">{sidebar}</div>

      {menuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <button
            type="button"
            aria-label="Fechar menu"
            className="absolute inset-0 bg-black/50"
            onClick={() => setMenuOpen(false)}
          />
          <div className="relative h-full shadow-2xl">{sidebar}</div>
        </div>
      )}

      <main className="min-h-[calc(100%-4rem)] overflow-auto p-4 md:h-full md:flex-1 md:overflow-auto md:p-6">
        <Outlet />
      </main>
    </div>
  );
}
